import React, { Fragment } from 'react';
import { useField } from 'formik';
import { Listbox as HeadlessListbox, ListboxButton as HeadlessListboxButton, ListboxOptions as HeadlessListboxOptions, ListboxOption as HeadlessListboxOption, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

function Listbox({
  name,
  value,
  onChange,
  options = [],
  multiple = false,
  placeholder = 'Select an option',
  disabled,
  selectAll = false,
  selectAllLabel = 'Select all',
}) {
  return (
    <HeadlessListbox
      name={name}
      value={value}
      onChange={onChange}
      multiple={multiple}
      disabled={disabled}
    >
      <HeadlessListboxButton
        className={clsx(
          'text-left appearance-none block w-full py-1.5 px-2 text-gray-800 rounded border-none shadow-input focus:shadow-input-focus relative pr-8',
          { '!text-gray-300': disabled },
        )}
      >
        {({ value, open }) => (
          <div>
            {(() => {
              if (multiple) {
                const filteredValue = value?.filter(v => options.some(o => o.value === v));

                if (filteredValue?.length === 0) {
                  return <div>{placeholder}</div>;
                }

                return (
                  <div className="truncate space-x-1">
                    {filteredValue.map(v => (
                      <span key={v.value} className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">
                        {options.find(o => o.value === v).name}
                      </span>
                    ))}
                  </div>
                );
              } else {
                const selectedOption = options.find(option => option.value === value);

                if (!selectedOption) {
                  return <div>{placeholder}</div>;
                }

                return <div>{selectedOption.name}</div>;
              }
            })()}

            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 pointer-events-none">
              {open ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </div>
          </div>
        )}
      </HeadlessListboxButton>

      <div className="relative">
        <Transition as="div" className="relative z-20">
          <HeadlessListboxOptions
            className={clsx(
              'absolute mt-1 z-50 w-full appearance-none block text-gray-800 text-left border-none shadow-input bg-white outline-none p-px rounded max-h-64 overflow-auto',
              'transition ease-out duration-100',
              'data-[closed]:opacity-0 data-[closed]:scale-95',
              'data-[open]:opacity-100 data-[open]:scale-100',
            )}
          >
            {multiple && selectAll && (
              <HeadlessListboxOption
                onClick={e => {
                  e.preventDefault();

                  if (value?.length === options.length) {
                    onChange([]);
                  } else {
                    onChange(options.map(option => option.value));
                  }
                }}
                className="relative cursor-default select-none py-1.5 flex items-center hover:bg-gray-50 first:rounded-t"
              >
                <>
                  <div className="w-8 flex items-center justify-center">
                    {value?.length === options.length ? (
                      <span className="bg-chocolate-1000 h-3 w-3" />
                    ) : (
                      <span className="bg-gray-300 h-3 w-3" />
                    )}
                  </div>

                  <span className={`block truncate`}>
                    {selectAllLabel} ({options.length})
                  </span>
                </>
              </HeadlessListboxOption>
            )}

            {options.map(option => (
              <HeadlessListboxOption
                key={option.value}
                value={option.value}
                className="relative cursor-default select-none py-1.5 flex items-center hover:bg-gray-50 last:rounded-b"
              >
                {({ selected }) => (
                  <>
                    <div className="w-8 flex items-center justify-center">
                      {selected ? (
                        <span className="bg-chocolate-1000 h-3 w-3" />
                      ) : (
                        <span className="bg-gray-300 h-3 w-3" />
                      )}
                    </div>

                    <span className={`block truncate`}>{option.name}</span>
                  </>
                )}
              </HeadlessListboxOption>
            ))}
          </HeadlessListboxOptions>
        </Transition>
      </div>
    </HeadlessListbox>
  );
}

export default function ListboxField(props) {
  const [field, formikMeta, formikHelpers] = useField(props);

  return (
    <Listbox
      name={field.name}
      value={field.value}
      onChange={value => {
        formikHelpers.setValue(value);

        if (props.onChange) {
          props.onChange(value);
        }
      }}
      options={props.options}
      multiple={props.multiple}
      placeholder={props.placeholder}
      disabled={props.disabled}
      selectAll={props.selectAll}
      selectAllLabel={props.selectAllLabel}
    >
      {props.children}
    </Listbox>
  );
}
